import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Input,
  Stack,
  Typography,
  Avatar,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { FriendProfile, useFollowStore } from "../../store/followStore";
import { useAuthStore } from "../../store/authStore";
import { useNavigate } from "react-router-dom";

interface FriendsListProps {
  onBack: () => void;
}

export const FriendsList = ({ onBack }: FriendsListProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { profile } = useAuthStore();
  const { friendProfiles, friendsLoading, fetchFollowingProfiles } =
    useFollowStore();
  const navigate = useNavigate();
  useEffect(() => {
    if (profile?.id) {
      fetchFollowingProfiles(profile.id);
    }
  }, [profile?.id, fetchFollowingProfiles]);

  const filteredFriends = friendProfiles.filter((friend) => {
    const query = searchQuery.toLowerCase();
    const nameMatch = friend.full_name?.toLowerCase().includes(query) ?? false;
    const usernameMatch = friend.username.toLowerCase().includes(query);
    return nameMatch || usernameMatch;
  });

  const navigateToProfile = (friend: FriendProfile) => {
    // Implement navigation to friend's profile page
    // For example: navigate(`/profile/${friend.id}`);
    navigate(`/profile/${friend.id}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton
          aria-label="Back to profile"
          size="sm"
          variant="plain"
          onClick={onBack}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography level="h4">Friends</Typography>
      </Stack>

      <Input
        placeholder="Search friends..."
        startDecorator={<SearchIcon />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
      />

      {friendsLoading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : friendProfiles.length === 0 ? (
        <Typography level="body-md" sx={{ textAlign: "center", py: 4 }}>
          You haven't followed anyone yet
        </Typography>
      ) : filteredFriends.length === 0 ? (
        <Typography level="body-md" sx={{ textAlign: "center", py: 4 }}>
          No friends match your search
        </Typography>
      ) : (
        <Stack spacing={1}>
          {filteredFriends.map((friend) => (
            <Card
              key={friend.id}
              variant="outlined"
              sx={{ p: 1.5, cursor: "pointer" }}
              onClick={() => navigateToProfile(friend)}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  src={friend.avatar_url || undefined}
                  sx={{ width: 40, height: 40 }}
                >
                  {(friend.full_name || friend.username)[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography level="title-sm">
                    {friend.full_name || friend.username}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: "neutral.500" }}>
                    @{friend.username}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};
